import React from 'react';
import { useSelector } from 'react-redux';
import { useFinancialCheckupData } from 'components/FinancialCheckup/YourFinances/yourFinancesUtils';

import DebtCategories from 'components/FinancialCheckup/YourFinances/Debts/DebtCategories';
import ActionCard, { ActionCardVariant, ActionCardSize } from 'components/Common/Cards/ActionCard';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';
import { getApplicationData } from 'selectors/getApplicationData';
import { HardOfferData } from 'handlers/applicationData';
import { RoutePath } from 'enums/Routes';
import { useNavigate } from 'hooks/useNavigate';
import usePursueLoanOffer from 'hooks/usePursueLoanOffer';
import { isConfirmedLoanOffer, isProbableLoanOffer } from 'components/FinancialCheckup/loanOfferAccess';
import ProbableOfferMark from 'components/FinancialCheckup/ProbableOfferMark/ProbableOfferMark';
import { OFFER_AVAILABLE_FROM_DASHBOARD_STATE } from 'utils/navigationStates';

import styles from './DebtTrackerSection.module.scss';

const PLANNERY_LOAN_CARD_EVENT_NAME = 'plannery_loan_card_clicked';

const DebtTrackerSection: React.FC = () => {
  const { totalDebt, keepItDebts, payOffDebts } = useFinancialCheckupData();
  const { application } = useSelector(getApplicationData);
  const navigate = useNavigate();
  const { pursueLoan } = usePursueLoanOffer();
  const hardOffer = (application?.hardOffer ?? {}) as Partial<HardOfferData>;
  const moneySaved = hardOffer?.offerSummary?.moneySaved;
  const isProbableOffer = isProbableLoanOffer(application?.status);
  const showPlanneryLoanCard =
    (isConfirmedLoanOffer(application?.status) || isProbableOffer) && moneySaved && moneySaved > 0;

  const handlePlanneryLoanCardClick = async () => {
    analytics.track(PLANNERY_LOAN_CARD_EVENT_NAME);
    if (isProbableOffer) {
      await pursueLoan();
      return;
    }
    navigate(RoutePath.OfferAvailable, OFFER_AVAILABLE_FROM_DASHBOARD_STATE);
  };

  if (!totalDebt) return null;

  return (
    <div className={styles.nextStepSection}>
      {showPlanneryLoanCard && (
        <ActionCard
          variant={ActionCardVariant.Gradient}
          size={ActionCardSize.Compact}
          className={styles.planneryLoanCard}
          onClick={handlePlanneryLoanCardClick}
          showChevron
        >
          <ActionCard.Col className={styles.planneryLoanCard}>
            <ActionCard.Title className={styles.planneryLoanCardTitle}>
              Save {formatMonetaryAmount(moneySaved, true)} in interest
              {isProbableOffer && <ProbableOfferMark />}
            </ActionCard.Title>
            <ActionCard.Description className={styles.planneryLoanCardDescription}>
              Pay off debt with a Plannery loan.
            </ActionCard.Description>
          </ActionCard.Col>
        </ActionCard>
      )}
      <div className={styles.labelContainer}>
        <p className={styles.sectionLabel}>Total</p>
        <p className={styles.totalAmount}>{formatMonetaryAmount(totalDebt, true)}</p>
      </div>
      <DebtCategories
        debtItems={[...keepItDebts, ...payOffDebts]}
        totalDebt={totalDebt}
        className={styles.debtCategories}
      />
    </div>
  );
};

export default DebtTrackerSection;
